'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/cn';
import { DarkMode } from '@/components/Header/darkMode';
import { HEADER_NAV_LINKS } from '@/components/Header/navLinks';
import { LinkButton } from '@/components/ui/link';
import PhadoniaLogo from '@/svg/phadonia.svg';

const cq = {
	grid: 'ph-header-wide__grid',
	hamburger: 'ph-header-wide__hamburger',
	navRow: 'ph-header-wide__nav-row',
	navList: 'ph-header-wide__nav-list',
} as const;

type THeaderProps = {
	wideBreakpointRem: number;
};

export function Header({ wideBreakpointRem }: THeaderProps) {
	const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
	const headerRef = useRef<HTMLElement | null>(null);

	const styles = `
		@container (min-width: ${wideBreakpointRem}rem) {
			.${cq.grid} {
				grid-template-columns: 1fr auto 1fr;
				gap: 0;
			}
			.${cq.hamburger} {
				display: none;
			}
			.${cq.navRow} {
				display: contents;
			}
			.${cq.navList} {
				display: flex;
			}
		}
	`;

	useEffect(() => {
		const header = headerRef.current;
		if (!header) return;
		const handler = () => setIsNavOpen(header.matches(':popover-open'));
		header.addEventListener('toggle', handler);
		return () => header.removeEventListener('toggle', handler);
	}, []);

	return (
		<header ref={headerRef} id="header" className=' pointer-events-none breakout-wrapper [--header-height:1.5rem] bg-transparent w-full top-7.5 z-50 [&:popover-open_>_div]:grid-rows-[1fr]' popover="">
			<style>{styles}</style>
			<a
				href='#main-content'
				className='skip-link col-sm pointer-events-auto rounded-full bg-body-50 px-[1.5em] py-[0.75em] text-body-900 border border-body-100 dark:bg-body-950 dark:text-body-100 dark:border-body-900'
			>
				Skip to content
			</a>
			<div
				className='col-sm @container overflow-clip relative grid grid-rows-[0fr] px-[calc(var(--header-height)*1.5)] py-[calc(var(--header-height)*0.75)] corner-shape-6 transition-[grid-template-rows] duration-300 h-fit'
			>
				<div className='absolute bg-body-50/25 w-screen h-[200%] left-1/2 top-0 border border-body-50/25 -translate-x-1/2 -translate-y-1/4 backdrop-blur-xl -z-10 dark:bg-body-950/25 dark:border-body-950/25'></div>
				<div className={cn('pointer-events-auto grid items-center gap-8 min-h-(--header-height)', cq.grid)}>
					<div className='flex items-center justify-between'>
						<LinkButton
							className='rounded-xs outline-offset-4'
							href='/'
							aria-label='Home, Phadonia'
						>
							<PhadoniaLogo className='h-(--header-height) *:fill-body-950 dark:*:fill-body-50' aria-hidden="true" />
						</LinkButton>
						{/* !!! I did not make this hamburgerBtn, https://codepen.io/ainalem/pen/wvKOEMV !!! */}
						<LinkButton
							className={cn('hamburger-button rounded-sm', cq.hamburger)}
							aria-label={isNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
							aria-expanded={isNavOpen}
							aria-controls="header"
							onClick={() => setIsNavOpen((prev) => !prev)}
							command="toggle-popover"
							commandfor="header"
						>
							<svg
								className='size-(--header-height) scale-150 text-body-900 dark:text-body-100 *:transition-[stroke-dashoffset,stroke-dasharray] *:duration-500'
								viewBox='0 0 100 100'
								xmlns='http://www.w3.org/2000/svg'
								stroke='currentColor'
								strokeWidth={6.4}
								fill='none'
								aria-hidden="true"
							>
								<path
									className='path1'
									d='M 20,29.000046 H 80.000231 C 80.000231,29.000046 94.498839,28.817352 94.532987,66.711331 94.543142,77.980673 90.966081,81.670246 85.259173,81.668997 79.552261,81.667751 75.000211,74.999942 75.000211,74.999942 L 25.000021,25.000058'
								/>
								<path className='path2' d='M 20,50 H 80' />
								<path
									className='path3'
									d='M 20,70.999954 H 80.000231 C 80.000231,70.999954 94.498839,71.182648 94.532987,33.288669 94.543142,22.019327 90.966081,18.329754 85.259173,18.331003 79.552261,18.332249 75.000211,25.000058 75.000211,25.000058 L 25.000021,74.999942'
								/>
							</svg>
						</LinkButton>
					</div>
					<div className={cn('flex justify-between items-end', cq.navRow)}>
						<nav aria-label='Main navigation'>
							<ul className={cn('grid gap-x-6 gap-y-4', cq.navList)}>
								{Object.entries(HEADER_NAV_LINKS).map(([label, path]) => (
									<li key={label}>
										<LinkButton className='text-body-800 dark:text-body-200 rounded-xs outline-offset-4' href={path}>
											{label}
										</LinkButton>
									</li>
								))}
							</ul>
						</nav>
						<div className='justify-self-end [&_svg]:size-(--header-height)'>
							<DarkMode />
						</div>
					</div>
				</div>
			</div>
		</header>
	);
}
